import React, { useCallback, useEffect, useRef, useState } from 'react'
import { EvalApiService } from '../../services/EvalApiService'
import { TagList } from './TagList'

interface TagEditorProps {
  token: string
  tags: string[]
  onChange: (tags: string[]) => void | Promise<void>
  disabled?: boolean
}

export const TagEditor: React.FC<TagEditorProps> = ({ token, tags, onChange, disabled = false }) => {
  const [allTags, setAllTags] = useState<string[] | null>(null) // null = not loaded yet
  const [inputValue, setInputValue] = useState('')
  const [open, setOpen] = useState(false)
  const [highlighted, setHighlighted] = useState(-1)
  const [saving, setSaving] = useState(false)
  const wrapperRef = useRef<HTMLDivElement>(null)

  const loadTags = useCallback(() => {
    EvalApiService.listDatasetTags(token)
      .then((data) => setAllTags(data))
      .catch(() => setAllTags([]))
  }, [token])

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false)
        setHighlighted(-1)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const normalized = inputValue.trim().toLowerCase()
  const loaded = allTags !== null
  const available = allTags ?? []

  // Tags from the system that aren't already on this dataset and match the current input
  const filtered = available.filter((t) => !tags.includes(t) && (normalized === '' || t.includes(normalized)))
  const showCreate = normalized.length > 0 && !available.includes(normalized) && !tags.includes(normalized)
  const totalOptions = filtered.length + (showCreate ? 1 : 0)

  const addTag = useCallback(
    async (tag: string) => {
      const norm = tag.trim().toLowerCase()
      if (!norm || tags.includes(norm)) return
      setSaving(true)
      await onChange([...tags, norm])
      setSaving(false)
      setInputValue('')
      setHighlighted(-1)
    },
    [tags, onChange]
  )

  const removeTag = useCallback(
    async (tag: string) => {
      setSaving(true)
      await onChange(tags.filter((t) => t !== tag))
      setSaving(false)
    },
    [tags, onChange]
  )

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        setOpen(true)
        return
      }
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlighted((h) => Math.min(h + 1, totalOptions - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlighted((h) => Math.max(h - 1, -1))
    } else if (e.key === 'Escape') {
      setOpen(false)
      setHighlighted(-1)
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (highlighted >= 0 && highlighted < filtered.length) {
        addTag(filtered[highlighted])
      } else if (highlighted === filtered.length && showCreate) {
        addTag(inputValue.trim())
      } else if (normalized) {
        const existing = available.find((t) => t === normalized)
        addTag(existing ?? inputValue.trim())
      }
    }
  }

  const isDisabled = disabled || saving

  if (disabled) {
    return <TagList tags={tags} />
  }

  return (
    <div className="eval-tag-input-row" ref={wrapperRef}>
      {tags.map((tag) => (
        <span key={tag} className="eval-tag">
          {tag}
          <button
            type="button"
            className="eval-tag-remove"
            disabled={saving}
            onClick={() => removeTag(tag)}
          >
            ×
          </button>
        </span>
      ))}
      <div style={{ position: 'relative' }}>
        <input
          className="eval-form-input eval-tag-input"
          type="text"
          value={inputValue}
          placeholder="Add tag…"
          disabled={isDisabled}
          onChange={(e) => {
            setInputValue(e.target.value)
            setHighlighted(-1)
          }}
          onFocus={() => {
            if (allTags === null) loadTags()
            setOpen(true)
          }}
          onKeyDown={handleKeyDown}
        />
        {open && (
          <div className="eval-tag-dropdown">
            {!loaded && (
              <div className="eval-tag-option eval-tag-option-hint">Loading…</div>
            )}
            {loaded && filtered.map((tag, i) => (
              <div
                key={tag}
                className={`eval-tag-option${highlighted === i ? ' highlighted' : ''}`}
                onMouseDown={(e) => { e.preventDefault(); addTag(tag) }}
                onMouseEnter={() => setHighlighted(i)}
              >
                {tag}
              </div>
            ))}
            {loaded && showCreate && (
              <div
                className={`eval-tag-option eval-tag-create${highlighted === filtered.length ? ' highlighted' : ''}`}
                onMouseDown={(e) => { e.preventDefault(); addTag(inputValue.trim()) }}
                onMouseEnter={() => setHighlighted(filtered.length)}
              >
                Create "{normalized}"
              </div>
            )}
            {loaded && totalOptions === 0 && (
              <div className="eval-tag-option eval-tag-option-hint">
                {normalized ? 'No matching tags' : 'Type to create a tag'}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
